import './MetricsTable.scss';
import cn from 'classnames';
import {
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import * as uuid from 'uuid';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { SortInfo } from '@blisspointmedia/bpm-types/dist/Performance';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { HeatMap } from '../../utils/colors';
import { makeMemoizedGetter } from '../../utils/data';
import { useExperimentFlag } from '../../utils/experiment-context';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { useScrollbarSizes } from '../../utils/hooks/useDOMHelpers';
import { Placement, PlacementType, Size, SizeType } from '../designConstants';
import { Img } from '../Img';
import { InteractiveDiv } from '../InteractiveEl';
import { CellRenderer, CornerLocation, StickyTable, SuperTopItemType } from '../StickyTable';
import { CreativeTray } from './CreativeTray/CreativeTray';
import {
  calculateDisplayedMetricsMap,
  calculateHeatMapRanges,
  DisplayedMetrics,
  generateHeatMaps,
  IntermediateMetrics
} from './CreativeTray/creativeTrayUtils';
import { DimensionHeaderLabel } from './DimensionHeaderLabel';
import {
  AdInfoRow,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  getColumnMeta,
  getExportedData,
  getFooterType,
  getHeatMap,
  getHeatMapInfo as getHeatMapInfoUtil,
  getTextWidth,
  measureHeaderLabel,
  TEXT_WIDTH_MARGIN,
  sortData,
  SuperHeaderItem,
  TEXT_FONT_SIZE_MAP,
  TEXT_SIZE_MAP
} from './metrics-table-utils';
import { PercentAOIPlaceholder } from './PercentAOIPlaceholder/PercentAOIPlaceholder';

export interface OverrideParams {
  colPadding?: number;
  colWidth?: number | number[];
  dimensionColWidth?: number | number[];
  maxColWidth?: number;
  rowHeight?: number | number[];
  rowPadding?: number;
  headerTextHeight?: number;
}

// Lazy export — `getExportedData` only runs when called, so heavy pages don't
// pay an O(N×M) build per sort/data update.
export interface MetricsTableHandle {
  getExportedData: () => (string | number)[][];
  scrollToTop: () => void;
}

interface MetricsTableProps {
  aggregateData: CellData[];
  columnMetaDataMap?: ColumnMetaDataMap;
  dataColumns: Column[];
  defaultSorting?: SortInfo[];
  dimensionColumns: DimensionColumn[];
  dimensionData: Record<string, CellData>[];
  disableMultiSorting?: boolean;
  // Caps the table's vertical height. Above this, rows scroll inside.
  // Below, the table is exactly as tall as content.
  maxHeight?: number;
  // When true, the table measures its container and fills the available
  // vertical space. Use this for flex-laid-out panels where the parent height
  // is set by layout (a flex item, an explicit `height` CSS value, or absolute
  // positioning). In an auto-height parent the measurement is circular and
  // the table degrades to a provisional height — don't enable this mode there.
  fillParentHeight?: boolean;
  overrideParams?: OverrideParams;
  percentAOIPlaceholder?: number;
  role?: number; // We don't want to use to redux here because we cannot use it in storybook testing
  // When false, column headers are purely informational — no sort icon, no click-to-sort,
  // default cursor. Use for tables whose row order is meaningful in a way sorting would
  // break (e.g. rows that group a parent with its children).
  sortable?: boolean;
  superHeaders?: SuperTopItemType<SuperHeaderItem>[];
  tableData: CellData[][];
  // Optional mask — render only these original-row indices. Lets pages apply
  // filters without forcing a constructTableData rebuild.
  visibleRowIndices?: ReadonlySet<number>;
  // 'Widget' code paths inside this file are intentionally kept around so it can
  // be re-enabled with a one-line type change later.
  variant?: 'Metrics' | 'SparkChart'; // | 'Widget' — no current use cases, needs work, see Storybook
  // Row count at/above which we let StickyTable's built-in windowing kick in.
  // Below this we still render every row, matching the prior behavior for small tables.
  virtualizeThreshold?: number;
  // Escape hatch for callers that need a fixed pixel width (print/snapshot/etc.).
  // When omitted (the common case), the table measures its container and fills
  // available horizontal space.
  fixedWidth?: number;
  kpiName?: string;
  groupedSegments?: Record<string, GroupedSegment[]>;
  ref?: Ref<MetricsTableHandle>;
}

// Used on the very first render, before ResizeObserver fires. Never painted —
// `useLayoutEffect` in `useContainerSize` schedules a re-render with the real
// measurement before the browser paints. The constants exist only to keep
// layout math from working with NaN on the initial pass.
const PROVISIONAL_WIDTH = 1200;
const PROVISIONAL_HEIGHT = 500;
// Floor for the measured container width. Below this the table becomes
// degenerate; we'd rather force internal horizontal scroll than render an
// unusable strip.
const MIN_TABLE_WIDTH = 320;

export const MetricsTable = ({
  aggregateData = [],
  columnMetaDataMap = {},
  dataColumns = [],
  defaultSorting = [],
  dimensionColumns = [],
  dimensionData = [],
  disableMultiSorting = true,
  maxHeight,
  fillParentHeight = false,
  overrideParams = {},
  percentAOIPlaceholder,
  role = 10,
  sortable = true,
  superHeaders: superHeadersInput,
  tableData = [],
  variant = 'Metrics',
  virtualizeThreshold = 50,
  fixedWidth,
  kpiName,
  groupedSegments = {},
  visibleRowIndices,
  ref
}: MetricsTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredContainerSize = useContainerSize(containerRef);
  // Instance-scoped canvas-measurement cache. Lives for the component's
  // lifetime (so sort/override re-renders stay warm) and is garbage-collected
  // on unmount. `getTextWidth` enforces a soft size cap to bound memory if
  // many filter/refetch cycles accumulate distinct strings. The
  // `react-hooks/refs` rule flags `.current` reads during render because the
  // common bug is expecting mutations to propagate through that read — here
  // we explicitly want the opposite, since the cache is a side-channel for
  // canvas measurements with no effect on render output.
  // eslint-disable-next-line react-hooks/refs
  const textWidthCache = useRef(new Map<string, number>()).current;
  // StickyTable overwrites this on mount via useImperativeHandle.
  const scrollToRef = useRef<(row: number, col: number) => void>(() => {});
  // Geist loads async via `font-display: swap`. On a cold load canvas
  // measures against the fallback font before Geist arrives, leaving
  // dimension columns too narrow once Geist swaps in. `document.fonts`
  // status/ready are unreliable — the browser only fetches Geist once
  // something renders text in it. Explicitly load each weight we measure,
  // then clear the cache and flip state to force a re-measure.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) {
      setFontsReady(true);
      return undefined;
    }
    let cancelled = false;
    const measuredWeights = ['400 14px Geist', '500 14px Geist', '600 14px Geist'];
    Promise.all(measuredWeights.map(spec => document.fonts.load(spec)))
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        textWidthCache.clear();
        setFontsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [textWidthCache]);
  // StickyTable always reserves a vertical scrollbar's worth of space inside
  // its grid when content fits. Mirror that here so MetricsTable's cellPadding
  // inflation lands inside the reserved area instead of overflowing into a
  // spurious horizontal scrollbar.
  const scrollbarSizes = useScrollbarSizes(containerRef);
  const resolvedWidth = (() => {
    if (fixedWidth !== undefined) return fixedWidth;
    if (measuredContainerSize?.width) {
      return Math.max(measuredContainerSize.width, MIN_TABLE_WIDTH);
    }
    return PROVISIONAL_WIDTH;
  })();
  const {
    colPadding: overrideColPadding,
    colWidth: overrideColWidth,
    dimensionColWidth: overrideDimensionColWidth,
    maxColWidth: overrideMaxColWidth,
    rowHeight: overrideRowHeight,
    rowPadding: overrideRowPadding,
    headerTextHeight: overrideHeaderTextHeight
  } = overrideParams;
  const COL_DIVIDER_BORDER_WIDTH = 2;
  const DIMENSION_COLUMNS_RIGHT_MARGIN =
    overrideColPadding != null || overrideColWidth != null ? 0 : 16;
  const FOOTER_CONTAINER_BOTTOM_PADDING = 16;
  const FOOTER_CONTAINER_TOP_PADDING = 8;
  const FOOTER_TABLE_PADDING = 32;
  const FOOTER_HEIGHT = 63 + FOOTER_TABLE_PADDING;
  const FOOTER_PADDING = variant === 'SparkChart' ? 0 : 4;
  const HEADER_FOOTER_PADDING = variant === 'SparkChart' ? 0 : 3;
  const HEADER_SUBHEADER_GAP = 4;
  const HEADER_TEXT_HEIGHT = overrideHeaderTextHeight != null ? overrideHeaderTextHeight : 23;
  const LEFT_BORDER_WIDTH = 4;
  const LOGO_WIDTH = 50;
  const MAX_COL_PADDING = variant === 'Metrics' ? 72 : 24;
  const computeMaxDimWidth = () => {
    if (overrideMaxColWidth != null) return 2 * overrideMaxColWidth;
    if (variant === 'Metrics') return 224;
    if (variant === 'SparkChart') return 112;
    return 56;
  };
  const MAX_DIM_WIDTH = computeMaxDimWidth();
  const MAX_PRODUCT_NAME_WIDTH = 1.5 * MAX_DIM_WIDTH;
  const computeMaxGridColWidth = () => {
    if (overrideMaxColWidth != null) return overrideMaxColWidth;
    // @ts-expect-error -- 'Widget' is temporarily removed from the variant union; see file header.
    return variant === 'Widget' ? 56 : 136;
  };
  const MAX_GRID_COL_WIDTH = computeMaxGridColWidth();
  const computeMinColPadding = () => {
    if (variant === 'Metrics') return 12;
    if (variant === 'SparkChart') return 8;
    return 4;
  };
  const MIN_COL_PADDING = computeMinColPadding();
  const MIN_HEADER_CONTAINER_HEIGHT = 43;
  // @ts-expect-error -- 'Widget' is temporarily removed from the variant union; see file header.
  const MIN_ROW_HEIGHT = variant === 'Widget' ? 24 : 28;
  const computeRowPadding = () => {
    if (overrideRowPadding != null) return overrideRowPadding;
    if (variant === 'Metrics') return 5;
    if (variant === 'SparkChart') return 4;
    return 0;
  };
  const ROW_PADDING = computeRowPadding();
  const SUB_HEADER_HEIGHT = 24;
  const SUPER_HEADER_PADDING = 16;
  const THREE_LINE_HEADER_TEXT_HEIGHT = 53;
  const THREE_LINE_ROW_HEIGHT = 58;
  const THUMBNAIL_WIDTH = 90;
  const TWO_LINE_HEADER_TEXT_HEIGHT = 38;
  const TWO_LINE_ROW_HEIGHT = 43;

  const headerTextHeights = useMemo(
    () => ({
      oneLine: HEADER_TEXT_HEIGHT,
      twoLine: TWO_LINE_HEADER_TEXT_HEIGHT,
      threeLine: THREE_LINE_HEADER_TEXT_HEIGHT
    }),
    [HEADER_TEXT_HEIGHT, TWO_LINE_HEADER_TEXT_HEIGHT, THREE_LINE_HEADER_TEXT_HEIGHT]
  );

  const dataHeaders: Column[] = useMemo(
    () =>
      (dataColumns || [])
        .filter(col => (col && col.role ? col.role >= role : true))
        .map(column => ({
          ...column,
          id: column.id ?? uuid.v4()
        })),
    [dataColumns, role]
  );

  const dimensionDataHeaders: DimensionColumn[] = useMemo(
    () =>
      (dimensionColumns || [])
        .filter(col => (col && col.role ? col.role >= role : true))
        .map(column => ({
          ...column,
          id: column.id ?? uuid.v4()
        })),
    [dimensionColumns, role]
  );

  const hasAggregateData = aggregateData.length > 0;
  const hasDimensionData = dimensionData.length > 0 && dimensionDataHeaders.length > 0;
  const TABLE_RIGHT_PADDING = hasDimensionData ? 16 : 0;
  const hasDataHeaders = dataHeaders.length > 0;
  const hasSuperHeaders = useMemo(
    () => superHeadersInput != null && superHeadersInput.length > 0,
    [superHeadersInput]
  );
  const SUPER_HEADER_HEIGHT = hasSuperHeaders ? 26 + 2 * 16 : 0;

  const [sorting, setSorting] = useState<SortInfo[]>(
    defaultSorting && defaultSorting.length > 0
      ? defaultSorting
      : dimensionDataHeaders
          .filter(d => d.dimensionTypeName === 'Date')
          .map(d => ({ id: d.id, asc: true }))
  );

  // CREATIVE TRAY STATE
  const [creativeTrayOpen, setCreativeTrayOpen] = useState(false);
  const [creativeTrayMetrics, setCreativeTrayMetrics] = useState<DisplayedMetrics>({
    spend: -1,
    cpc: -1,
    ctr: -1,
    ccr: -1,
    volume: -1,
    revenue: -1,
    roas: -1,
    cpx: -1
  });
  const [adInfo, setAdInfo] = useState<AdInfoRow>({
    company: '',
    platform: '',
    ad_name: '',
    ad_id: '',
    account_id: ''
  });
  const [heatMaps, setHeatMaps] = useState<Record<keyof DisplayedMetrics, HeatMap>>(
    {} as Record<keyof DisplayedMetrics, HeatMap>
  );
  // END CREATIVE TRAY STATE

  const [selectedThumbnailSize, setSelectedThumbnailSize] = useState<SizeType>(Size.SM);

  const shouldEnableMotionReplacement = useExperimentFlag('enableMotionReplacement');
  const shouldEnableMetricsTableSegmentGroups = useExperimentFlag(
    'enableMetricsTableSegmentGroups'
  );

  const addSorting = useCallback((id: string, disableMulti = false) => {
    //  If a user selects  an already-sorted column, it flips it if it's descending, and removes it otherwise.
    setSorting(prevSorting => {
      let newSorting: SortInfo[] = [];
      let sawSelf = false;
      for (const sortItem of prevSorting) {
        if (sortItem.id === id) {
          sawSelf = true;
          // If it's descending, then flip. Otherwise, skip.
          if (!sortItem.asc) {
            newSorting.push({ id, asc: true });
          }
        } else {
          newSorting.push(sortItem);
        }
      }
      if (!sawSelf) {
        if (disableMulti) {
          // If we pick an entirely new column to sort, we want to clear out
          // all the current sorting.
          newSorting = [{ id, asc: false }];
        } else {
          newSorting.push({ id, asc: false });
        }
      }
      return newSorting;
    });
  }, []);

  const { sortedDimensionData, sortedTableData, sortedIndices } = useMemo(
    () =>
      sortData(
        dataHeaders,
        dimensionData,
        dimensionDataHeaders,
        sorting,
        tableData,
        hasDimensionData,
        visibleRowIndices
      ),
    [
      dataHeaders,
      dimensionData,
      dimensionDataHeaders,
      sorting,
      tableData,
      hasDimensionData,
      visibleRowIndices
    ]
  );
  const numDimCols = dimensionDataHeaders.length;
  const numDataCols = dataHeaders.length;
  const numCols = numDataCols + numDimCols;
  const numRows = tableData.length;

  useImperativeHandle(
    ref,
    () => ({
      getExportedData: () =>
        getExportedData({
          columnMetaDataMap,
          dataHeaders,
          dimensionDataHeaders,
          numDataCols,
          numDimCols,
          sortedDimensionData,
          sortedTableData
        }),
      scrollToTop: () => scrollToRef.current(0, 0)
    }),
    [
      columnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      numDataCols,
      numDimCols,
      sortedDimensionData,
      sortedTableData
    ]
  );

  // CREATIVE TRAY DATA PROCESSING
  const displayedMetricsMap = useMemo(() => {
    if (!shouldEnableMotionReplacement) {
      return {};
    }

    const metricsMap: Record<string, DisplayedMetrics> = {};

    sortedTableData.forEach((row, rowIndex) => {
      const adNameCell = sortedDimensionData[rowIndex]?.['Ad Name'];
      const adName = adNameCell?.label;

      if (!adName) {
        return;
      }

      const intermediateMetrics: IntermediateMetrics = {
        spend: -1,
        impressions: -1,
        clicks: -1,
        volume: -1,
        revenue: -1,
        roas: -1,
        cpx: -1
      };

      dataHeaders.forEach((header, colIndex) => {
        const cellData = row[colIndex];
        if (cellData && typeof cellData.value === 'number') {
          intermediateMetrics[header.dataVarName] = cellData.value;
        }
      });

      const displayedMetrics = calculateDisplayedMetricsMap(intermediateMetrics);

      metricsMap[adName] = displayedMetrics;
    });
    return metricsMap;
  }, [shouldEnableMotionReplacement, dataHeaders, sortedDimensionData, sortedTableData]);

  const heatMapRanges = useMemo(() => {
    if (!shouldEnableMotionReplacement) {
      return null;
    }
    return calculateHeatMapRanges(displayedMetricsMap);
  }, [shouldEnableMotionReplacement, displayedMetricsMap]);

  useEffect(() => {
    if (!shouldEnableMotionReplacement || !heatMapRanges) {
      return;
    }
    const newHeatMaps = generateHeatMaps(heatMapRanges);
    setHeatMaps(newHeatMaps);
  }, [shouldEnableMotionReplacement, heatMapRanges]);
  // END CREATIVE TRAY DATA PROCESSING

  const heatMap = useMemo(
    () => getHeatMap(dataHeaders, sortedTableData),
    [dataHeaders, sortedTableData]
  );

  const memoizedHeatMapGetter = useMemo(
    () =>
      makeMemoizedGetter({
        calculate: ({ columnIndex, value }: { columnIndex: number; value: number }) =>
          getHeatMapInfoUtil(columnIndex, columnMetaDataMap, dataHeaders, heatMap, value)
      }),
    [columnMetaDataMap, dataHeaders, heatMap]
  );

  const getHeatMapInfo = useCallback(
    ({ columnIndex, value }: { columnIndex: number; value: number }) => {
      if (!sortedTableData) {
        return { backgroundColor: 'transparent', outline: 'none' };
      }
      return memoizedHeatMapGetter({ columnIndex, value });
    },
    [memoizedHeatMapGetter, sortedTableData]
  );

  const innerCellRenderer = (
    innerCellContent: ReactElement,
    overlayText: string | undefined,
    overlayPlacement: PlacementType = Placement.AUTO
  ): ReactElement =>
    overlayText ? (
      // Portal to the body and allow flipping so the tooltip isn't clipped by an ancestor's
      // `overflow: hidden` layout and reorients near an edge.
      <OverlayTrigger
        placement={overlayPlacement}
        flip
        container={document.body}
        overlay={<Tooltip id={overlayText}>{overlayText}</Tooltip>}
      >
        {innerCellContent}
      </OverlayTrigger>
    ) : (
      innerCellContent
    );

  const superHeaders: SuperTopItemType<SuperHeaderItem>[] = useMemo(
    () =>
      superHeadersInput && superHeadersInput.length > 0
        ? superHeadersInput
        : [
            {
              data: { text: '', leftDivider: false, rightDivider: false },
              span: numDataCols
            }
          ],
    [numDataCols, superHeadersInput]
  );

  // Sort-invariant layout pass. Column widths, header height, and per-row
  // heights only depend on cell content, not row order, so we iterate the
  // unsorted `tableData` / `dimensionData` and key row heights by the
  // *original* row index. A second, cheap memo below permutes those heights
  // into display-index order using `sortedIndices` from `sortData`. Net
  // effect: sorting no longer reruns the expensive `getTextWidth` loop.
  const staticLayout = useMemo(() => {
    const leftColWidthsAcc = {};
    const colWidthsAcc = {};
    const heatMappingWidthsAcc = {};
    const rowHeightsByOriginalIndex: Record<number, number> = {};
    let headerHeight: number | undefined;

    const THUMBNAIL_SIZES = {
      small: { width: 42, height: 50 },
      medium: { width: 100, height: 100 },
      large: { width: 180, height: 180 }
    };

    if (dimensionData && tableData) {
      for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
        const numColsInRow = Math.max(numDataCols, numDimCols);
        for (let colIndex = 0; colIndex < numColsInRow; colIndex++) {
          const dimColIndex = Math.min(colIndex, numDimCols - 1);
          const dataColIndex = Math.min(colIndex, numDataCols - 1);
          // Calculate Header/Footer Widths
          if (rowIndex === 0) {
            // Dimension Header Widths
            const leftHeader = (dimensionDataHeaders ?? [])[dimColIndex];
            const genericDimColumnMetaData = columnMetaDataMap[leftHeader.dimensionTypeName];
            const dimensionHeaderLabel =
              leftHeader.label ?? (genericDimColumnMetaData ?? {}).prettyName;
            if (dimensionHeaderLabel) {
              const { labelWidth, headerHeight: labelHeight } = measureHeaderLabel({
                label: dimensionHeaderLabel,
                columnWidth: MAX_DIM_WIDTH,
                allowMultiLine: variant !== 'SparkChart',
                heights: headerTextHeights,
                textWidthCache
              });
              headerHeight = Math.max(headerHeight ?? 0, labelHeight);
              if (leftColWidthsAcc[dimColIndex]) {
                const maxWidthForColumn =
                  Object.keys(groupedSegments).includes(leftHeader.dimensionTypeName) &&
                  shouldEnableMetricsTableSegmentGroups
                    ? MAX_PRODUCT_NAME_WIDTH
                    : MAX_DIM_WIDTH;
                leftColWidthsAcc[dimColIndex] = Math.min(
                  maxWidthForColumn,
                  Math.max(leftColWidthsAcc[dimColIndex], labelWidth)
                );
              } else {
                leftColWidthsAcc[dimColIndex] = labelWidth;
              }
            }
            // Data Header Widths
            const dataHeader = (dataHeaders ?? [])[dataColIndex];
            const genericDataColumnMetaData = columnMetaDataMap[dataHeader.dataVarName];
            const dataHeaderLabel =
              dataHeader.label ?? (genericDataColumnMetaData ?? {}).prettyName;
            if (dataHeaderLabel) {
              const { labelWidth, headerHeight: labelHeight } = measureHeaderLabel({
                label: dataHeaderLabel,
                columnWidth: MAX_GRID_COL_WIDTH,
                allowMultiLine: variant !== 'SparkChart',
                heights: headerTextHeights,
                textWidthCache
              });
              headerHeight = Math.max(headerHeight ?? 0, labelHeight);
              if (colWidthsAcc[dataColIndex]) {
                colWidthsAcc[dataColIndex] = Math.min(
                  MAX_GRID_COL_WIDTH,
                  Math.max(colWidthsAcc[dataColIndex], labelWidth)
                );
              } else {
                colWidthsAcc[dataColIndex] = labelWidth;
              }
            }
            // Data Footer Widths
            const aggregateDataLabel =
              aggregateData && aggregateData[dataColIndex] && aggregateData[dataColIndex].label
                ? aggregateData[dataColIndex].label
                : undefined;
            if (aggregateDataLabel) {
              const labelWidth = Math.max(
                Math.ceil(getTextWidth(aggregateDataLabel, 14, 501, 'Geist', textWidthCache)) +
                  TEXT_WIDTH_MARGIN, // Add some padding to avoid wrapping
                Math.ceil(getTextWidth('OVERALL', 14, 501, 'Geist', textWidthCache))
              );
              if (colWidthsAcc[dataColIndex]) {
                colWidthsAcc[dataColIndex] = Math.min(
                  MAX_GRID_COL_WIDTH,
                  Math.max(colWidthsAcc[dataColIndex], labelWidth)
                );
              } else {
                colWidthsAcc[dataColIndex] = labelWidth;
              }
            }
          }
          // Dimension Data Cells
          const dimHeader = (dimensionDataHeaders ?? [])[dimColIndex];
          // Include extra space for the scrollbar
          const genericColumnMetaData = columnMetaDataMap[dimHeader.dimensionTypeName];

          const isAdNameColumn = dimHeader.dimensionTypeName === 'Ad Name';
          const thumbnailSize = isAdNameColumn ? selectedThumbnailSize : undefined;
          const thumbnailDimensions = thumbnailSize ? THUMBNAIL_SIZES[thumbnailSize] : undefined;

          const extraImageGapWidth = dimHeader && dimHeader.icon === 'hasIcon' ? 14 : 0;
          const computeExtraImageWidth = () => {
            if (!dimHeader || dimHeader.icon !== 'hasIcon') return 0;
            if (genericColumnMetaData.iconStyle !== 'thumbnail') {
              return LOGO_WIDTH + extraImageGapWidth;
            }
            if (isAdNameColumn && thumbnailDimensions) {
              return thumbnailDimensions.width + extraImageGapWidth;
            }
            return THUMBNAIL_WIDTH + extraImageGapWidth;
          };
          const extraImageWidth = computeExtraImageWidth();
          const { label: dimensionCellLabel } =
            dimensionData[rowIndex][dimHeader.dimensionTypeName];
          if (dimensionCellLabel) {
            const maxWidthForColumn =
              Object.keys(groupedSegments).includes(dimHeader.dimensionTypeName) &&
              shouldEnableMetricsTableSegmentGroups
                ? MAX_PRODUCT_NAME_WIDTH
                : MAX_DIM_WIDTH;
            const labelWidth =
              Math.ceil(getTextWidth(dimensionCellLabel, 14, 600, 'Geist', textWidthCache)) +
              TEXT_WIDTH_MARGIN;
            const numLines = Math.ceil(labelWidth / maxWidthForColumn);
            // Set Row Heights
            if (
              dimHeader &&
              dimHeader.icon === 'hasIcon' &&
              genericColumnMetaData.iconStyle === 'thumbnail' &&
              isAdNameColumn &&
              thumbnailDimensions
            ) {
              const minHeightForThumbnail = thumbnailDimensions.height + 2 * ROW_PADDING;
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                minHeightForThumbnail
              );
            } else if (
              (numLines > 2 || ['hasIcon', 'iconOnly'].includes(dimHeader.icon as string)) &&
              variant !== 'SparkChart'
            ) {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING
              );
            } else if (numLines > 1 && variant !== 'SparkChart') {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                TWO_LINE_ROW_HEIGHT + 2 * ROW_PADDING
              );
            } else {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                MIN_ROW_HEIGHT + 2 * ROW_PADDING
              );
            }
            // Set Column Widths
            if (dimHeader.icon === 'iconOnly') {
              leftColWidthsAcc[dimColIndex] =
                genericColumnMetaData.iconStyle === 'thumbnail' ? THUMBNAIL_WIDTH : LOGO_WIDTH;
            } else if (leftColWidthsAcc[dimColIndex]) {
              leftColWidthsAcc[dimColIndex] = Math.min(
                maxWidthForColumn + extraImageWidth,
                Math.max(leftColWidthsAcc[dimColIndex], labelWidth + extraImageWidth)
              );
            } else {
              leftColWidthsAcc[dimColIndex] = labelWidth;
            }
          }
          // Table Data Cells
          const { label: dataCellLabel } = tableData[rowIndex][dataColIndex];
          if (dataCellLabel) {
            const labelWidth =
              Math.ceil(getTextWidth(dataCellLabel, 14, 400, 'Geist', textWidthCache)) +
              TEXT_WIDTH_MARGIN;
            const numLines = Math.ceil(labelWidth / MAX_GRID_COL_WIDTH);
            // Set Row Heights
            if (numLines > 2 && variant !== 'SparkChart') {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING
              );
            } else if (numLines > 1 && variant !== 'SparkChart') {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                TWO_LINE_ROW_HEIGHT + 2 * ROW_PADDING
              );
            } else {
              rowHeightsByOriginalIndex[rowIndex] = Math.max(
                rowHeightsByOriginalIndex[rowIndex] ?? 0,
                MIN_ROW_HEIGHT + 2 * ROW_PADDING
              );
            }
            // Set Column Widths
            if (colWidthsAcc[dataColIndex]) {
              const extra = variant === 'Metrics' ? 1 : 0;
              colWidthsAcc[dataColIndex] = Math.min(
                MAX_GRID_COL_WIDTH,
                Math.max(colWidthsAcc[dataColIndex] + extra, labelWidth)
              );
            } else {
              colWidthsAcc[dataColIndex] = labelWidth;
            }
            // Set Heatmapping Widths
            const heatMappingWidth = labelWidth + 28;
            if (heatMappingWidthsAcc[dataColIndex]) {
              heatMappingWidthsAcc[dataColIndex] = Math.min(
                MAX_GRID_COL_WIDTH,
                Math.max(heatMappingWidthsAcc[dataColIndex], heatMappingWidth)
              );
            } else {
              heatMappingWidthsAcc[dataColIndex] = heatMappingWidth;
            }
          }
        }
      }
    }
    if (overrideDimensionColWidth != null) {
      for (let colIndex = 0; colIndex < Object.keys(leftColWidthsAcc).length; colIndex++) {
        const overrideWidth: number = Array.isArray(overrideDimensionColWidth)
          ? overrideDimensionColWidth[colIndex]
          : overrideDimensionColWidth;
        if (!Number.isNaN(overrideWidth)) {
          colWidthsAcc[colIndex] = overrideWidth;
        }
      }
    }
    if (overrideColWidth != null) {
      for (let colIndex = 0; colIndex < Object.keys(colWidthsAcc).length; colIndex++) {
        const overrideWidth = Array.isArray(overrideColWidth)
          ? overrideColWidth[colIndex]
          : overrideColWidth;
        if (!Number.isNaN(overrideWidth)) {
          colWidthsAcc[colIndex] = overrideWidth;
          if (heatMappingWidthsAcc[colIndex] != null) {
            heatMappingWidthsAcc[colIndex] = Math.min(
              heatMappingWidthsAcc[colIndex],
              overrideWidth
            );
          }
        }
      }
    }
    return {
      leftColWidths: Object.keys(leftColWidthsAcc).length ? leftColWidthsAcc : undefined,
      colWidths: Object.keys(colWidthsAcc).length ? colWidthsAcc : undefined,
      heatMappingWidths: Object.keys(heatMappingWidthsAcc).length
        ? heatMappingWidthsAcc
        : undefined,
      headerHeight,
      rowHeightsByOriginalIndex
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dimensionData,
    tableData,
    overrideDimensionColWidth,
    overrideColWidth,
    numRows,
    numDataCols,
    numDimCols,
    dimensionDataHeaders,
    columnMetaDataMap,
    dataHeaders,
    aggregateData,
    MAX_DIM_WIDTH,
    variant,
    headerTextHeights,
    MAX_GRID_COL_WIDTH,
    MAX_PRODUCT_NAME_WIDTH,
    ROW_PADDING,
    MIN_ROW_HEIGHT,
    selectedThumbnailSize,
    shouldEnableMetricsTableSegmentGroups,
    groupedSegments,
    textWidthCache,
    // `textWidthCache` is a stable `useRef` — clearing it mutates in place,
    // so the deps array can't detect it. `fontsReady` is the primitive flip
    // that actually invalidates the memo and triggers a re-measure.
    fontsReady
  ]);

  const { leftColWidths, colWidths, heatMappingWidths } = staticLayout;

  // Sort-dependent step: cheap O(n) permutation of per-original-row heights
  // into display-index keys, plus the `overrideRowHeight` pass that used to
  // live inside the big layout memo. No `getTextWidth` work happens here.
  const rowHeights = useMemo(() => {
    const { headerHeight, rowHeightsByOriginalIndex } = staticLayout;
    const result: Record<string, number> = {};
    if (headerHeight !== undefined) {
      result.header = headerHeight;
    }
    for (let displayIdx = 0; displayIdx < sortedIndices.length; displayIdx++) {
      const rowHeightForDisplay = rowHeightsByOriginalIndex[sortedIndices[displayIdx]];
      if (rowHeightForDisplay !== undefined) {
        result[displayIdx] = rowHeightForDisplay;
      }
    }
    if (overrideRowHeight != null) {
      for (const rowIndex of Object.keys(result)) {
        result[rowIndex] = Array.isArray(overrideRowHeight)
          ? overrideRowHeight[rowIndex]
          : overrideRowHeight;
      }
    }
    return Object.keys(result).length ? result : undefined;
  }, [staticLayout, sortedIndices, overrideRowHeight]);

  const leftWidth = useMemo(() => {
    if (!leftColWidths) return 0;
    let sum = LEFT_BORDER_WIDTH;
    Object.keys(leftColWidths).forEach(colIndex => {
      if (leftColWidths[colIndex]) {
        sum += leftColWidths[colIndex];
      }
    });
    return hasDimensionData ? sum : 0;
  }, [hasDimensionData, leftColWidths]);

  const gridWidth = useMemo(() => {
    if (!colWidths) return 0;
    const colKeys = Object.keys(colWidths);
    let sum = 0;
    colKeys.forEach(colIndex => {
      if (colWidths[colIndex]) {
        const columnWidth = colWidths[colIndex] ?? MAX_GRID_COL_WIDTH + 2 * MAX_COL_PADDING;
        const computeBorderWidth = () => {
          if (dataHeaders && dataHeaders[colIndex] && dataHeaders[colIndex].divider) {
            return 2 * COL_DIVIDER_BORDER_WIDTH;
          }
          return parseInt(colIndex, 10) === colKeys.length - 1 ? 0.25 : 0.5;
        };
        const borderWidth = computeBorderWidth();
        sum += columnWidth + borderWidth;
      }
    });
    return sum;
  }, [MAX_COL_PADDING, MAX_GRID_COL_WIDTH, colWidths, dataHeaders]);

  const cellPadding = useMemo(() => {
    if (overrideColPadding != null) {
      return overrideColPadding;
    }
    if (leftWidth && hasDimensionData && dataHeaders) {
      const remainingWidth =
        resolvedWidth -
        leftWidth -
        gridWidth -
        TABLE_RIGHT_PADDING -
        DIMENSION_COLUMNS_RIGHT_MARGIN -
        scrollbarSizes.width;
      const clampedCellPadding = Math.min(
        MAX_COL_PADDING,
        Math.max(MIN_COL_PADDING, Math.floor(remainingWidth / (2 * numCols)))
      );
      return clampedCellPadding;
    }
    return MIN_COL_PADDING;
  }, [
    overrideColPadding,
    leftWidth,
    hasDimensionData,
    dataHeaders,
    MIN_COL_PADDING,
    resolvedWidth,
    gridWidth,
    TABLE_RIGHT_PADDING,
    DIMENSION_COLUMNS_RIGHT_MARGIN,
    MAX_COL_PADDING,
    numCols,
    scrollbarSizes.width
  ]);

  const columnWidths = useCallback(
    colIndex => {
      const columnWidth =
        colWidths && colWidths[colIndex]
          ? colWidths[colIndex] + 2 * cellPadding
          : MAX_GRID_COL_WIDTH + 2 * MAX_COL_PADDING;
      const borderWidth =
        dataHeaders && dataHeaders[colIndex] && dataHeaders[colIndex].divider
          ? 2 * COL_DIVIDER_BORDER_WIDTH
          : 0.25;
      if (overrideColPadding != null || overrideColWidth != null) {
        const overrideWidth = Array.isArray(overrideColWidth)
          ? overrideColWidth[colIndex]
          : overrideColWidth;
        return (overrideWidth ?? columnWidth) + borderWidth;
      }
      return (
        columnWidth +
        borderWidth +
        (colIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0) +
        (colIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0)
      );
    },
    [
      colWidths,
      cellPadding,
      MAX_GRID_COL_WIDTH,
      MAX_COL_PADDING,
      dataHeaders,
      overrideColPadding,
      overrideColWidth,
      DIMENSION_COLUMNS_RIGHT_MARGIN,
      numDataCols,
      TABLE_RIGHT_PADDING
    ]
  );

  const hasSubHeader = useMemo(() => {
    if (Boolean(percentAOIPlaceholder) || percentAOIPlaceholder === 0) {
      return true;
    }
    return dataHeaders ? dataHeaders.some(({ subHeader }) => Boolean(subHeader)) : false;
  }, [dataHeaders, percentAOIPlaceholder]);

  // Sum of every row's intrinsic height — header area + super-headers + each
  // data row + footer. Drives the auto-grow path (no `fillParentHeight`, no
  // `maxHeight`) and provides the upper bound for the `maxHeight` cap path.
  // Doesn't depend on container measurement and is the same value before/after
  // ResizeObserver fires. The sum is permutation-invariant, so we read from
  // the sort-invariant `staticLayout.rowHeightsByOriginalIndex` rather than
  // the per-sort `rowHeights` permutation — sorting no longer reruns this.
  const contentHeight = useMemo(() => {
    let total = 0;
    if (hasSuperHeaders) {
      total += SUPER_HEADER_HEIGHT;
    }
    if (hasAggregateData) {
      total += FOOTER_HEIGHT;
    }
    if (typeof overrideRowHeight === 'number') {
      return total + overrideRowHeight * numRows;
    }
    const fallbackRowHeight = MIN_ROW_HEIGHT + 2 * ROW_PADDING;
    const { rowHeightsByOriginalIndex } = staticLayout;
    for (let i = 0; i < numRows; i++) {
      const heightForRow = Array.isArray(overrideRowHeight)
        ? overrideRowHeight[i]
        : rowHeightsByOriginalIndex[i];
      total += heightForRow ?? fallbackRowHeight;
    }
    return total;
  }, [
    numRows,
    staticLayout,
    overrideRowHeight,
    hasSuperHeaders,
    hasAggregateData,
    SUPER_HEADER_HEIGHT,
    FOOTER_HEIGHT,
    MIN_ROW_HEIGHT,
    ROW_PADDING
  ]);

  const headerContainerHeight = useMemo(
    () =>
      hasDataHeaders
        ? Math.max(
            rowHeights && rowHeights.header
              ? // rowHeights.header is the text height; the container adds ROW_PADDING top and bottom.
                rowHeights.header +
                  2 * ROW_PADDING +
                  (hasSubHeader ? HEADER_SUBHEADER_GAP + SUB_HEADER_HEIGHT : 0)
              : MIN_HEADER_CONTAINER_HEIGHT,
            MIN_HEADER_CONTAINER_HEIGHT
          )
        : 0,
    [hasDataHeaders, hasSubHeader, rowHeights, ROW_PADDING]
  );

  // Three height paths, in priority order:
  //   1. `fillParentHeight` — measure container, clamp to `maxHeight` if set
  //   2. `maxHeight` set, auto-grow path — table grows to content, caps at maxHeight
  //   3. neither — auto-grow with no cap, table is as tall as content
  // A classic (non-overlay) horizontal scrollbar, shown when the columns are wider than the table,
  // takes its height out of the scroller's visible area. Add it so the last row isn't pushed behind
  // a spurious vertical scrollbar. Read from the DOM since overflow depends on the rendered widths.
  const [horizontalScrollbarHeight, setHorizontalScrollbarHeight] = useState(0);
  useLayoutEffect(() => {
    const scroller = containerRef.current?.querySelector<HTMLElement>('.sticky-table');
    const height =
      scroller && scroller.scrollWidth > scroller.clientWidth
        ? scroller.offsetHeight - scroller.clientHeight
        : 0;
    setHorizontalScrollbarHeight(height);
  }, [resolvedWidth, gridWidth, leftWidth, cellPadding, numRows]);
  const fullContentHeight = headerContainerHeight + contentHeight + horizontalScrollbarHeight;
  const resolvedHeight = (() => {
    if (fillParentHeight) {
      if (!measuredContainerSize?.height) return PROVISIONAL_HEIGHT;
      return maxHeight !== undefined
        ? Math.min(measuredContainerSize.height, maxHeight)
        : measuredContainerSize.height;
    }
    return maxHeight !== undefined ? Math.min(fullContentHeight, maxHeight) : fullContentHeight;
  })();

  const dividerLines = useMemo(() => {
    const dividerLinesMap: Record<string | number, boolean> = {};
    if (dataHeaders) {
      for (let i = 0; i < numDataCols; i++) {
        // If we are on the first column, ignore since we already have a dimension divider
        const dividerKey = i === 0 ? undefined : `${i - 1}_${i}`;
        if (dataHeaders[i].divider && dividerKey) {
          dividerLinesMap[dividerKey] = true;
        }
      }
    }
    if (superHeaders) {
      let leftColumnIndex = 0;
      for (let i = 0; i < superHeaders.length; i++) {
        const rightColumnIndex = leftColumnIndex + superHeaders[i].span - 1;
        const superHeader = superHeaders[i];
        const leftDividerKey =
          leftColumnIndex === 0 ? undefined : `${leftColumnIndex - 1}_${leftColumnIndex}`;
        const rightDividerKey =
          rightColumnIndex === numDataCols - 1
            ? rightColumnIndex
            : `${rightColumnIndex}_${rightColumnIndex + 1}`;
        if (superHeader.data.leftDivider && leftDividerKey) {
          dividerLinesMap[leftDividerKey] = true;
        }
        if (superHeader.data.rightDivider) {
          dividerLinesMap[rightDividerKey] = true;
        }
        leftColumnIndex += superHeader.span;
      }
    }
    return dividerLinesMap;
  }, [dataHeaders, numDataCols, superHeaders]);

  const rowHeight = useCallback(
    rowIndex => (rowHeights && rowHeights[rowIndex] ? rowHeights[rowIndex] : MIN_ROW_HEIGHT),
    [MIN_ROW_HEIGHT, rowHeights]
  );

  const dataHeaderRenderer: CellRenderer<Column> = useCallback(
    ({ columnIndex, classes = [], data, style = {} }) => {
      const { label: columnLabel, subHeader } = data;
      const header = dataHeaders[columnIndex];
      const computeOverlayText = () => {
        const meta = columnMetaDataMap && columnMetaDataMap[header.dataVarName];
        if (!meta) return undefined;
        return meta.customMetaData ? meta.customMetaData.description : meta.overlayText;
      };
      const overlayText = computeOverlayText();
      const overlayPlacement =
        columnMetaDataMap && columnMetaDataMap[header.dataVarName]
          ? columnMetaDataMap[header.dataVarName].overlayPlacement
          : undefined;
      const mySorting = sorting.find(({ id }) => id === header.id);
      const contentType =
        columnIndex != null &&
        header &&
        header.dataVarName &&
        columnMetaDataMap[header.dataVarName] &&
        columnMetaDataMap[header.dataVarName].contentType
          ? columnMetaDataMap[header.dataVarName].contentType
          : 'number';
      const alignItems = contentType === 'text' ? 'flex-start' : 'flex-end';
      const headerHeight = Math.max(
        HEADER_TEXT_HEIGHT,
        rowHeights && rowHeights.header ? rowHeights.header : HEADER_TEXT_HEIGHT
      );
      const justifyContent = alignItems;
      const maxWidth =
        MAX_GRID_COL_WIDTH +
        cellPadding * 2 +
        COL_DIVIDER_BORDER_WIDTH +
        (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
      const minHeight = MIN_ROW_HEIGHT;
      const paddingBottom = ROW_PADDING;
      const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
      const paddingRight =
        cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
      const paddingTop = ROW_PADDING;
      const subHeaderHeight = SUB_HEADER_HEIGHT;
      const textAlign = contentType === 'text' ? 'left' : 'right';
      const label =
        columnLabel ||
        (columnMetaDataMap[header.dataVarName]
          ? columnMetaDataMap[header.dataVarName].prettyName
          : header.dataVarName);
      const headerText = (
        <div
          className={`text ${TEXT_SIZE_MAP[variant]}`}
          style={{
            alignItems: 'center',
            display: 'flex',
            height: headerHeight,
            textAlign,
            width: 'fit-content'
          }}
        >
          {label}
        </div>
      );
      // We want to maintain the same spacing if there is just one subheader in the table,
      // so we add a placeholder subheader if there isn't one but make the text transparent
      const subHeaderText = subHeader && (
        <div
          className='sub-header text'
          style={{
            height: subHeaderHeight,
            justifyContent,
            textAlign,
            width: 'fit-content'
          }}
        >
          {subHeader}
        </div>
      );
      const sortIconSide = contentType === 'text' ? 'right' : 'left';
      const sortIcon = sortable && contentType !== 'logo' && (
        <div className={`sort-icon-container ${mySorting == null ? '' : 'sorted'}`}>
          {mySorting && mySorting.asc ? <MdArrowUpward /> : <MdArrowDownward />}
        </div>
      );
      const headerContainerJustifyContent = subHeader ? 'space-between' : 'flex-end';
      const cursor = sortable && contentType !== 'logo' ? 'pointer' : 'default';
      return innerCellRenderer(
        <div
          style={{
            ...style,
            alignItems,
            height: headerContainerHeight,
            justifyContent: headerContainerJustifyContent,
            maxWidth,
            minHeight,
            paddingBottom,
            paddingLeft,
            paddingRight,
            paddingTop,
            textAlign
          }}
          className={cn(...classes, 'table-header-container', {
            end: columnIndex === numDataCols - 1,
            'left-divider': dividerLines[`${columnIndex - 1}_${columnIndex}`],
            'right-divider': dividerLines[`${columnIndex}_${columnIndex + 1}`],
            'right-end-divider': dividerLines[columnIndex]
          })}
        >
          <InteractiveDiv
            className='table-header'
            onClick={() => {
              if (sortable && contentType !== 'logo') {
                addSorting(header.id, disableMultiSorting);
              }
            }}
            style={{
              alignItems,
              cursor,
              justifyContent,
              textAlign
            }}
          >
            {sortIconSide === 'right' ? headerText : sortIcon}
            {sortIconSide === 'right' ? sortIcon : headerText}
          </InteractiveDiv>
          {(Boolean(percentAOIPlaceholder) || percentAOIPlaceholder === 0) &&
            header.dataVarName.toLowerCase().includes('percentincremental') && (
              <div
                style={{
                  width: '100%'
                }}
              >
                <PercentAOIPlaceholder percentage={percentAOIPlaceholder as number} />
              </div>
            )}
          {hasSubHeader && subHeaderText}
        </div>,
        overlayText,
        overlayPlacement
      );
    },
    [
      dataHeaders,
      sorting,
      rowHeights,
      MAX_GRID_COL_WIDTH,
      cellPadding,
      MIN_ROW_HEIGHT,
      ROW_PADDING,
      DIMENSION_COLUMNS_RIGHT_MARGIN,
      numDataCols,
      TABLE_RIGHT_PADDING,
      columnMetaDataMap,
      variant,
      headerContainerHeight,
      dividerLines,
      percentAOIPlaceholder,
      hasSubHeader,
      addSorting,
      disableMultiSorting,
      HEADER_TEXT_HEIGHT,
      sortable
    ]
  );

  const cellRenderer: CellRenderer<CellData> = useCallback(
    ({ classes = [], columnIndex = 0, data, rowIndex = 0, style = {} }) => {
      const { bold, label, overlayText, textColor, value } = data;
      const ourClasses = [...classes, 'body-cell'];
      if (columnIndex === 0) {
        ourClasses.push('start');
      }
      if (columnIndex === numDataCols - 1) {
        ourClasses.push('end');
      }
      if (rowIndex === numRows - 1) {
        ourClasses.push('last-row');
      }
      if (dividerLines[`${columnIndex - 1}_${columnIndex}`]) {
        ourClasses.push('left-divider');
      }
      if (dividerLines[`${columnIndex}_${columnIndex + 1}`]) {
        ourClasses.push('right-divider');
      }
      if (dividerLines[columnIndex]) {
        ourClasses.push('right-end-divider');
      }
      const { backgroundColor, outline } =
        typeof value === 'number'
          ? getHeatMapInfo({
              columnIndex,
              value
            })
          : { backgroundColor: 'transparent', outline: 'none' };
      const header = dataHeaders[columnIndex];
      const contentType =
        columnIndex != null &&
        header &&
        header.dataVarName &&
        columnMetaDataMap[header.dataVarName] &&
        columnMetaDataMap[header.dataVarName].contentType
          ? columnMetaDataMap[header.dataVarName].contentType
          : 'number';
      const cellHeight =
        colWidths &&
        colWidths[columnIndex] &&
        rowHeights &&
        (rowIndex || rowIndex === 0) &&
        rowHeights[rowIndex]
          ? rowHeights[rowIndex] - 2 * ROW_PADDING
          : 'fit-content';
      const justifyContent =
        contentType === 'text' && backgroundColor !== 'transparent' ? 'flex-start' : 'flex-end';
      const maxWidth =
        MAX_GRID_COL_WIDTH +
        cellPadding * 2 +
        COL_DIVIDER_BORDER_WIDTH +
        (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
      const minHeight = MIN_ROW_HEIGHT;
      // Padding in between cells (column,row)
      const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
      const paddingRight =
        cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
      // We need some extra padding on the first row to account for the border
      const bodyCellPaddingTop = rowIndex === 0 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
      const bodyCellPaddingBottom =
        rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
      // Inner Cell Bottom/Top Padding inside of the background color
      const paddingBottom =
        variant !== 'SparkChart' ? (MIN_ROW_HEIGHT - TEXT_FONT_SIZE_MAP[variant]) / 2 : 0;
      const paddingTop = paddingBottom;
      const textAlign =
        contentType === 'text' && backgroundColor !== 'transparent' ? 'left' : 'right';
      const heatMapWidth =
        heatMappingWidths && heatMappingWidths[columnIndex]
          ? heatMappingWidths[columnIndex]
          : 'fit-content';
      const bodyCellStyling = {
        ...style,
        justifyContent,
        maxWidth,
        paddingBottom: bodyCellPaddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop: bodyCellPaddingTop
      };
      return (
        <div style={bodyCellStyling} className={ourClasses.join(' ')}>
          <div
            className='cell-wrapper'
            style={{
              backgroundColor,
              height: cellHeight,
              justifyContent,
              outline,
              width: heatMapWidth
            }}
          >
            {innerCellRenderer(
              <div
                className={cn('inner-cell', TEXT_SIZE_MAP[variant], {
                  text: contentType !== 'logo',
                  'heat-mapped': backgroundColor !== 'transparent' || outline !== 'none',
                  bold
                })}
                style={{
                  color: textColor,
                  justifyContent,
                  minHeight,
                  paddingBottom,
                  paddingTop,
                  textAlign,
                  width: heatMapWidth
                }}
              >
                {label}
              </div>,
              overlayText
            )}
          </div>
        </div>
      );
    },
    [
      cellPadding,
      columnMetaDataMap,
      colWidths,
      dataHeaders,
      DIMENSION_COLUMNS_RIGHT_MARGIN,
      dividerLines,
      getHeatMapInfo,
      HEADER_FOOTER_PADDING,
      heatMappingWidths,
      MAX_GRID_COL_WIDTH,
      MIN_ROW_HEIGHT,
      numDataCols,
      numRows,
      ROW_PADDING,
      rowHeights,
      TABLE_RIGHT_PADDING,
      variant
    ]
  );

  const dimensionHeaderRenderer = useCallback(
    (corner: CornerLocation) => {
      // Render Dimension Columns
      if (corner === 'nw') {
        const cornerClasses = ['dimension-corner-container'];
        if (superHeaders) {
          cornerClasses.push('withSuperHeaders');
        }
        let columnIndex = 0;
        const cornerHeight = headerContainerHeight;
        return (
          <div className={cornerClasses.join(' ')} style={{ height: cornerHeight }}>
            {dimensionDataHeaders.map(dimension => {
              const headerClasses = ['table-header'];
              const mySorting = sorting.find(({ id }) => id === dimension.id);
              const dimHeader = dimensionDataHeaders[columnIndex];
              const overlayText =
                dimHeader && columnMetaDataMap[dimHeader.dimensionTypeName]
                  ? columnMetaDataMap[dimHeader.dimensionTypeName].overlayText
                  : undefined;
              const contentType =
                dimHeader &&
                columnMetaDataMap[dimHeader.dimensionTypeName] &&
                columnMetaDataMap[dimHeader.dimensionTypeName].contentType
                  ? columnMetaDataMap[dimHeader.dimensionTypeName].contentType
                  : 'text';
              const iconStyle =
                dimHeader &&
                columnMetaDataMap[dimHeader.dimensionTypeName] &&
                columnMetaDataMap[dimHeader.dimensionTypeName].iconStyle
                  ? columnMetaDataMap[dimHeader.dimensionTypeName].iconStyle
                  : undefined;
              const computeSortIconSide = () => {
                if (contentType === 'text') return 'right';
                if (contentType === 'logo') return 'center';
                return 'left';
              };
              const sortIconSide = computeSortIconSide();
              const justifyContent =
                contentType === 'text' || contentType === 'logo' ? 'flex-start' : 'flex-end';
              const textAlign = contentType === 'text' || contentType === 'logo' ? 'left' : 'right';
              const headerHeight = Math.max(
                rowHeights && rowHeights.header ? rowHeights.header : HEADER_TEXT_HEIGHT,
                HEADER_TEXT_HEIGHT
              );
              const computeLabel = () => {
                if (dimension.label) return dimension.label;
                const meta = columnMetaDataMap[dimension.dimensionTypeName];
                return meta ? meta.prettyName : dimension.dimensionTypeName;
              };
              const label = computeLabel();
              const headerText = (
                <DimensionHeaderLabel
                  dimensionTypeName={dimension.dimensionTypeName}
                  label={label}
                  headerHeight={headerHeight}
                  textAlign={textAlign}
                  hasThumbnail={iconStyle === 'thumbnail'}
                  setSelectedThumbnailSize={setSelectedThumbnailSize}
                />
              );
              const headerContainerJustifyContent = 'flex-end';
              const extraImageWidth =
                dimHeader && dimHeader.icon === 'hasIcon' && iconStyle
                  ? (iconStyle === 'logo' ? 50 : 90) + 14
                  : 0;
              const maxContainerWidth = MAX_DIM_WIDTH + 2 * MAX_COL_PADDING + extraImageWidth;
              const padding = `${ROW_PADDING}px ${cellPadding}px`;
              const dimColWidth =
                leftColWidths && leftColWidths[columnIndex]
                  ? leftColWidths[columnIndex]
                  : 'fit-content';
              const sortIcon = sortable && contentType !== 'logo' && (
                <div className={`sort-icon-container ${mySorting == null ? '' : 'sorted'}`}>
                  {mySorting && mySorting.asc ? <MdArrowUpward /> : <MdArrowDownward />}
                </div>
              );
              const cursor = sortable && contentType !== 'logo' ? 'pointer' : 'default';
              const header = innerCellRenderer(
                <div
                  key={`left_header_col${columnIndex}`}
                  className='table-header-container'
                  style={{
                    height: cornerHeight,
                    justifyContent: headerContainerJustifyContent,
                    maxWidth: maxContainerWidth,
                    padding
                  }}
                >
                  <InteractiveDiv
                    className={headerClasses.join(' ')}
                    onClick={event => {
                      const target = event.target as Element;
                      const isDropdownClick =
                        target.closest('.ad-name-header-dropdown') ||
                        target.closest('.menu-row') ||
                        target.closest('.dropdown-menu-container');
                      if (sortable && contentType !== 'logo' && !isDropdownClick) {
                        addSorting(dimension.id, disableMultiSorting);
                      }
                    }}
                    style={{
                      cursor,
                      justifyContent,
                      width: dimColWidth
                    }}
                  >
                    {sortIconSide === 'right' ? headerText : sortIcon}
                    {sortIconSide === 'right' ? sortIcon : headerText}
                  </InteractiveDiv>
                </div>,
                overlayText
              );
              columnIndex++;
              return header;
            })}
          </div>
        );
      }
      return <div />; // We aren't rendering anything else for the other corners
    },
    [
      superHeaders,
      headerContainerHeight,
      dimensionDataHeaders,
      sorting,
      rowHeights,
      columnMetaDataMap,
      MAX_DIM_WIDTH,
      MAX_COL_PADDING,
      ROW_PADDING,
      cellPadding,
      leftColWidths,
      addSorting,
      disableMultiSorting,
      HEADER_TEXT_HEIGHT,
      sortable
    ]
  );

  const dimensionCellRenderer: CellRenderer<Record<string, CellData>> = useCallback(
    ({ classes = [], data, rowIndex = 0, style = {} }) => {
      const columns: ReactElement[] = [];
      const alignItems = 'center';
      let colIndex = 0;
      for (const header of dimensionDataHeaders) {
        const {
          content: contentOverride,
          label,
          overlayText,
          url: imageURL,
          adInfo: cellAdInfo
        } = data[header.dimensionTypeName];
        const altText = overlayText !== label ? overlayText : '';
        const genericColumnMetaData = columnMetaDataMap[header.dimensionTypeName];
        const iconStyle = genericColumnMetaData?.iconStyle;
        const url = iconStyle ? (imageURL ?? '') : imageURL;
        const thumbnailSize = iconStyle === 'thumbnail' ? selectedThumbnailSize : undefined;
        const computeContentType = () => {
          if (header && iconStyle && header.icon === 'iconOnly') return iconStyle;
          if (header && iconStyle && header.icon === 'hasIcon' && url !== '') {
            return `text ${iconStyle} ${iconStyle}-${thumbnailSize}`;
          }
          if (header && genericColumnMetaData && genericColumnMetaData.contentType) {
            return genericColumnMetaData.contentType;
          }
          return 'text';
        };
        const contentType = computeContentType();
        const dimCellHeight =
          leftColWidths &&
          leftColWidths[colIndex] &&
          rowHeights &&
          (rowIndex || rowIndex === 0) &&
          rowHeights[rowIndex]
            ? rowHeights[rowIndex]
            : 'fit-content';
        const computeJustifyContent = () => {
          if (contentType.includes('text')) return 'flex-start';
          if (contentType === 'logo' || contentType === 'iconOnly') return 'center';
          return 'flex-end';
        };
        const justifyContent = computeJustifyContent();
        const dimCellMaxHeight =
          leftColWidths &&
          leftColWidths[colIndex] &&
          rowHeights &&
          (rowIndex || rowIndex === 0) &&
          rowHeights[rowIndex]
            ? `${rowHeights[rowIndex]} !important`
            : THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING;
        const computeExtraImageWidth = () => {
          if (!(header && header.icon === 'hasIcon' && iconStyle)) return 0;
          return (iconStyle === 'logo' ? 50 : 90) + 14;
        };
        const extraImageWidth = computeExtraImageWidth();
        const maxWidth =
          (Object.keys(groupedSegments).includes(header.dimensionTypeName) &&
          shouldEnableMetricsTableSegmentGroups
            ? MAX_PRODUCT_NAME_WIDTH
            : MAX_DIM_WIDTH) +
          2 * cellPadding +
          extraImageWidth;
        // Padding in between cells (column,row)
        const padding = `${ROW_PADDING}px ${cellPadding}px`;
        // Inner Cell Bottom/Top Padding inside of the background color
        const paddingBottom =
          variant !== 'SparkChart' ? (MIN_ROW_HEIGHT - TEXT_FONT_SIZE_MAP[variant]) / 2 : 0;
        const paddingTop = paddingBottom;
        const computeTextAlign = () => {
          if (contentType.includes('text')) return 'left';
          if (contentType === 'logo' || contentType === 'iconOnly') return 'center';
          return 'right';
        };
        const textAlign = computeTextAlign();
        const innerCellWidth =
          leftColWidths && leftColWidths[colIndex] ? leftColWidths[colIndex] : 'fit-content';
        const isSegmentGroupCol =
          Object.keys(groupedSegments).includes(header.dimensionTypeName) &&
          shouldEnableMetricsTableSegmentGroups;
        const labelClass = `label ${isSegmentGroupCol ? 'segment-group' : ''} ${
          leftColWidths && leftColWidths[colIndex] ? '' : 'no-wrap'
        }`;
        const computeContent = () => {
          if (contentOverride != null) return contentOverride;
          if (url != null && header.icon === 'iconOnly') {
            return innerCellRenderer(
              <Img src={url} alt={altText} unloader={altText} />,
              overlayText
            );
          }
          if (url != null && url !== '' && header.icon) {
            return (
              <div className='dimension-cell-with-icon'>
                {innerCellRenderer(
                  <div className={iconStyle}>
                    <Img src={url} alt={altText} unloader={altText} />
                  </div>,
                  overlayText
                )}
                <div className={labelClass}>{label}</div>
              </div>
            );
          }
          return innerCellRenderer(<div className={labelClass}>{label}</div>, overlayText);
        };
        const content = computeContent();

        const isAdNameColumn = header.dimensionTypeName === 'Ad Name';
        const handleClick =
          isAdNameColumn && shouldEnableMotionReplacement
            ? () => {
                setCreativeTrayOpen(true);
                if (cellAdInfo) {
                  setAdInfo(cellAdInfo);
                } else {
                  setAdInfo({
                    company: '',
                    platform: '',
                    ad_name: '',
                    ad_id: '',
                    account_id: ''
                  });
                }
                const displayedMetrics = displayedMetricsMap[label];
                if (displayedMetrics) {
                  setCreativeTrayMetrics(displayedMetrics);
                }
              }
            : undefined;
        const cursor = isAdNameColumn && shouldEnableMotionReplacement ? 'pointer' : 'default';
        columns.push(
          <div
            key={`left_col${colIndex}_row${rowIndex}`}
            className='cell-wrapper'
            style={{
              alignItems,
              height: dimCellHeight,
              maxHeight: dimCellMaxHeight,
              maxWidth,
              padding
            }}
          >
            <InteractiveDiv
              className={`inner-cell ${contentType} ${TEXT_SIZE_MAP[variant]}`}
              style={{
                justifyContent,
                paddingBottom,
                paddingTop,
                textAlign,
                width: innerCellWidth,
                cursor
              }}
              onClick={handleClick}
            >
              {content}
            </InteractiveDiv>
          </div>
        );
        colIndex += 1;
      }
      const paddingTop = rowIndex === 0 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
      const paddingBottom =
        rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
      const rowContainerHeight =
        (rowHeights ? rowHeight(rowIndex) : MIN_ROW_HEIGHT) +
        (rowIndex === 0 || rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING : 0);
      const cellMaxHeight = rowHeights ? rowHeight(rowIndex) : MIN_ROW_HEIGHT;
      const minHeight = MIN_ROW_HEIGHT;
      if (rowIndex === numRows - 1) {
        classes.push('last-row');
      }
      return (
        <div
          style={{
            ...style,
            height: rowContainerHeight,
            maxHeight: cellMaxHeight,
            minHeight,
            paddingBottom,
            paddingTop
          }}
          className={[...classes, 'left-cell'].join(' ')}
        >
          {columns}
        </div>
      );
    },
    [
      cellPadding,
      columnMetaDataMap,
      dimensionDataHeaders,
      displayedMetricsMap,
      HEADER_FOOTER_PADDING,
      leftColWidths,
      MAX_DIM_WIDTH,
      MAX_PRODUCT_NAME_WIDTH,
      MIN_ROW_HEIGHT,
      numRows,
      ROW_PADDING,
      rowHeight,
      rowHeights,
      variant,
      setCreativeTrayOpen,
      shouldEnableMotionReplacement,
      selectedThumbnailSize,
      shouldEnableMetricsTableSegmentGroups,
      groupedSegments
    ]
  );

  const aggregateDataRenderer: CellRenderer<CellData> = useCallback(
    ({ classes = [], columnIndex, data, style = {} }) => {
      const { label } = data;
      const ourClasses = [...classes, 'subtotal-cell', 'body-cell'];
      if (columnIndex === 0) {
        ourClasses.push('start');
      }
      if (columnIndex === numDataCols - 1) {
        ourClasses.push('end');
      }
      const dataHeader = dataHeaders && columnIndex != null ? dataHeaders[columnIndex] : undefined;
      const contentType =
        columnIndex != null &&
        dataHeader &&
        dataHeader.dataVarName &&
        columnMetaDataMap[dataHeader.dataVarName] &&
        columnMetaDataMap[dataHeader.dataVarName].contentType
          ? columnMetaDataMap[dataHeader.dataVarName].contentType
          : 'number';
      const computeJustifyContent = () => {
        if (contentType === 'text') return 'flex-start';
        if (contentType === 'logo') return 'center';
        return 'flex-end';
      };
      const justifyContent = computeJustifyContent();
      const containerAlignItems = 'flex-start';
      const maxWidth = MAX_GRID_COL_WIDTH;
      const minHeight = MIN_ROW_HEIGHT;
      const paddingBottom = FOOTER_PADDING;
      const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
      const paddingRight =
        cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
      const paddingTop = FOOTER_PADDING;
      const computeTextAlign = () => {
        if (contentType === 'text') return 'left';
        if (contentType === 'logo') return 'center';
        return 'right';
      };
      const textAlign = computeTextAlign();
      const footerTextWidth = 'fit-content';
      const footerColWidth =
        colWidths && colWidths[columnIndex] ? colWidths[columnIndex] : 'fit-content';
      const footerRowHeight = FOOTER_HEIGHT;
      // Comparison columns' dataVarName is suffixed (`_other`/`_delta`) but
      // columnMetaDataMap is keyed by root metric name — getColumnMeta strips the
      // suffix so those columns share the primary metric's aggregator/requiredColumns
      // (and therefore the correct footer type) instead of resolving to undefined.
      const columnMetaData: ColumnMetaData | undefined = dataHeader
        ? getColumnMeta(columnMetaDataMap, dataHeader)
        : undefined;
      const footerType = getFooterType(columnMetaData, dataHeader);
      return (
        <div
          style={{
            ...style,
            alignItems: containerAlignItems,
            height: footerRowHeight,
            paddingLeft,
            paddingRight
          }}
          className={ourClasses.join(' ')}
        >
          <div
            className='cell-wrapper'
            style={{
              alignItems: containerAlignItems,
              height: footerRowHeight,
              justifyContent,
              maxWidth,
              minHeight,
              paddingBottom: FOOTER_CONTAINER_BOTTOM_PADDING,
              paddingTop: FOOTER_CONTAINER_TOP_PADDING
            }}
          >
            <div
              className={`footer inner-cell ${contentType !== 'logo' ? 'text' : ''} ${
                TEXT_SIZE_MAP[variant]
              }`}
              style={{
                alignItems: justifyContent,
                justifyContent,
                maxWidth,
                paddingBottom,
                paddingTop,
                textAlign,
                width: footerColWidth,
                minHeight
              }}
            >
              <div
                className='footer-type-container'
                style={{
                  justifyContent,
                  textAlign
                }}
              >
                {footerType}
              </div>
              <div
                className='footer-container'
                style={{
                  justifyContent,
                  maxWidth,
                  textAlign,
                  width: footerTextWidth
                }}
              >
                {label}
              </div>
            </div>
          </div>
        </div>
      );
    },
    [
      cellPadding,
      columnMetaDataMap,
      colWidths,
      dataHeaders,
      DIMENSION_COLUMNS_RIGHT_MARGIN,
      FOOTER_HEIGHT,
      FOOTER_PADDING,
      MAX_GRID_COL_WIDTH,
      MIN_ROW_HEIGHT,
      numDataCols,
      TABLE_RIGHT_PADDING,
      variant
    ]
  );

  const superHeaderRenderer: CellRenderer<SuperHeaderItem> = useCallback(
    ({ style = {}, classes = [], columnIndex, rowData }) => {
      const superData = rowData as SuperTopItemType<SuperHeaderItem>;
      const ourClasses = ['super-header-container'];
      const rightColumnIndex = columnIndex + superData.span - 1;
      if (dividerLines[`${columnIndex - 1}_${columnIndex}`]) {
        ourClasses.push('leftDivider');
      }
      if (dividerLines[`${rightColumnIndex}_${rightColumnIndex + 1}`]) {
        ourClasses.push('rightDivider');
      }
      if (dividerLines[rightColumnIndex]) {
        ourClasses.push('rightEndDivider');
      }
      if (superData && superData.data && superData.data.text.length) {
        ourClasses.push('has-text');
      }
      if (rightColumnIndex === numDataCols - 1) {
        ourClasses.push('end');
      }
      return (
        <div
          className={classes.join(' ')}
          style={{
            ...style,
            paddingTop: SUPER_HEADER_PADDING
          }}
        >
          <div
            style={{
              padding: `0px ${cellPadding}px ${SUPER_HEADER_PADDING}px ${cellPadding}px`
            }}
            className={ourClasses.join(' ')}
          >
            <div className='content inner-cell'>{superData.data.text}</div>
          </div>
        </div>
      );
    },
    [cellPadding, dividerLines, numDataCols]
  );

  const bottomData = hasAggregateData ? aggregateData : undefined;
  const leftData = hasDimensionData ? sortedDimensionData : undefined;
  const superTopData = hasSuperHeaders ? superHeaders : undefined;
  const topData = hasDataHeaders ? dataHeaders : undefined;

  const computeStickyTableLeftWidth = () => {
    if (leftColWidths && Object.keys(leftColWidths).length > 0) {
      return leftWidth + 2 * (cellPadding ?? 0) * Object.keys(leftColWidths).length;
    }
    return dimensionColumns ? dimensionColumns.length * MAX_DIM_WIDTH : 0;
  };
  const stickyTableLeftWidth = computeStickyTableLeftWidth();

  return (
    <div ref={containerRef} className='metrics-table'>
      {tableData.length === 0 ? (
        <div className='metrics-table-empty-state'>No data for the selected filters.</div>
      ) : (
        <StickyTable<
          CellData,
          Column,
          Record<string, CellData>,
          CellData,
          undefined,
          SuperHeaderItem
        >
          bottomData={bottomData}
          bottomRenderer={aggregateDataRenderer}
          bottomHeight={FOOTER_HEIGHT}
          cellRenderer={cellRenderer}
          columnWidth={columnWidths}
          cornerRenderer={dimensionHeaderRenderer}
          data={sortedTableData}
          height={resolvedHeight}
          leftData={leftData}
          leftRenderer={dimensionCellRenderer}
          leftWidth={stickyTableLeftWidth}
          overscan={numRows < virtualizeThreshold ? (numRows || 10) * 2 : 10}
          rowHeight={i => rowHeight(i)}
          scrollToRef={scrollToRef}
          superTopData={superTopData}
          superTopHeight={SUPER_HEADER_HEIGHT}
          superTopRenderer={superHeaderRenderer}
          topData={topData}
          topHeight={headerContainerHeight}
          topRenderer={dataHeaderRenderer}
          width={resolvedWidth}
        />
      )}
      {shouldEnableMotionReplacement && creativeTrayOpen && (
        <CreativeTray
          key={adInfo.ad_name}
          isOpen={creativeTrayOpen}
          onClose={() => setCreativeTrayOpen(false)}
          metrics={creativeTrayMetrics}
          heatMaps={heatMaps}
          adInfo={adInfo}
          kpiName={kpiName}
        />
      )}
    </div>
  );
};
